import { useId, type ReactNode } from "react";
import { X } from "lucide-react";
import { Button } from "../../atoms/index.js";
import { EYEBROW } from "../../lib/typography.js";
import { cn } from "../../lib/utils.js";

/* The header of a right-rail panel (rail-panel). RailPanel renders it, and it
   is exported on its own so a molecule that owns its own frame (RightDrawer,
   in adoption) can render the same header. RightDrawer's hand-built header
   set the recipe: px-5 py-4, a hairline under it, the eyebrow in the shared
   EYEBROW style above the title, badges under the title block. The close is
   a design-system Button (ghost, iconOnly, sm). */

export type RailPanelHeadingLevel = 2 | 3;

export interface RailPanelHeaderProps {
  /** The panel's name. Its heading labels the panel. */
  title: ReactNode;
  /** Short category text above the title, in the shared EYEBROW style. */
  eyebrow?: ReactNode;
  /** The line under the title (the Modal, Popover and EmptyState name). */
  description?: ReactNode;
  /** A visual before the title: an icon, a mark, an avatar. */
  leading?: ReactNode;
  /** The trailing action group, rendered before the close button. */
  actionsSlot?: ReactNode;
  /**
   * Heading level, default 2: a rail sits beside a page that already has its
   * h1. Use 3 when the rail is itself inside a section with an h2.
   */
  headingLevel?: RailPanelHeadingLevel;
  /** Shows the close button. Omit it and no close button renders. */
  onClose?: () => void;
  /**
   * The close button's accessible name. Defaults to "Close {title}" when the
   * title is a string, else "Close panel".
   */
  closeLabel?: string;
  /**
   * Where focus goes after the close button closes the panel. The button
   * lives inside the panel, so closing unmounts the focused element; without
   * this, focus falls to `<body>`. Called on the next tick, after `onClose`.
   */
  resolveReturnFocus?: () => HTMLElement | null;
  /**
   * The heading's id. Omit it and the header makes one. Pass it when an outer
   * element (a dialog, a section) is labelled by the heading.
   */
  titleId?: string;
  /** Content under the title block: badges, a status, a short meta row. */
  children?: ReactNode;
  /**
   * `comfortable` (default): RightDrawer's px-5 inset and a 2px gap above the
   * description. `compact`: a px-4 inset, so the header text starts on the
   * edge of a px-4 body, and the description sits flush under the title (the
   * access rail). The vertical padding and the type do not change.
   */
  density?: "comfortable" | "compact";
}

const ROOT = "flex shrink-0 items-start gap-3 border-b border-pb-border py-4";
const INSET = { comfortable: "px-5", compact: "px-4" } as const;
const TITLE = "min-w-0 text-base font-semibold leading-snug text-pb-text";
const DESCRIPTION = "text-dense-1 text-pb-text-muted";
const DESCRIPTION_GAP = { comfortable: "mt-0.5", compact: undefined } as const;
const CHILDREN = "mt-2 flex flex-wrap items-center gap-1.5";

function hasContent(node: ReactNode): boolean {
  return node != null && node !== false && node !== "";
}

/** The header of a right-rail panel: eyebrow, title, description, badges,
 *  trailing actions and a close button. */
export function RailPanelHeader({
  title,
  eyebrow,
  description,
  leading,
  actionsSlot,
  headingLevel = 2,
  onClose,
  closeLabel,
  resolveReturnFocus,
  titleId,
  children,
  density = "comfortable",
}: RailPanelHeaderProps) {
  const ownId = useId();
  const headingId = titleId ?? ownId;
  const Heading = `h${headingLevel}` as const;
  const label =
    closeLabel ??
    (typeof title === "string" ? `Close ${title}` : "Close panel");

  const close = (): void => {
    onClose?.();
    if (resolveReturnFocus) {
      window.setTimeout(() => resolveReturnFocus()?.focus(), 0);
    }
  };

  const heading = (
    <Heading id={headingId} className={TITLE}>
      {title}
    </Heading>
  );

  return (
    <header className={cn(ROOT, INSET[density])}>
      <div className="min-w-0 flex-1">
        {hasContent(eyebrow) ? (
          <div className={cn(EYEBROW, "mb-1")}>{eyebrow}</div>
        ) : null}
        {hasContent(leading) ? (
          <div className="flex min-w-0 items-center gap-2">
            <span className="flex shrink-0 items-center">{leading}</span>
            {heading}
          </div>
        ) : (
          heading
        )}
        {hasContent(description) ? (
          <p className={cn(DESCRIPTION_GAP[density], DESCRIPTION)}>
            {description}
          </p>
        ) : null}
        {hasContent(children) ? (
          <div className={CHILDREN}>{children}</div>
        ) : null}
      </div>
      {hasContent(actionsSlot) || onClose ? (
        <div className="-mt-1 flex shrink-0 items-center gap-1">
          {actionsSlot}
          {onClose ? (
            <Button
              variant="ghost"
              iconOnly
              size="sm"
              aria-label={label}
              onClick={close}
              className="-me-1"
            >
              <X aria-hidden className="h-4 w-4" />
            </Button>
          ) : null}
        </div>
      ) : null}
    </header>
  );
}
